import { useState } from 'react';
import type { DashboardDeadline } from '../../types';
import { pacificToday } from '../../utils/deadlineDates';

const ymd = (d: Date) => d.toISOString().slice(0, 10);
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Props {
  deadlines: DashboardDeadline[];
  onReschedule: (d: DashboardDeadline, due_date: string) => void;
}

export default function DeadlineCalendar({ deadlines, onReschedule }: Props) {
  // ESC1-TZ: opens on the PACIFIC month - at 6 PM PT on Oct 31, UTC is already November.
  const today = pacificToday();
  const [view, setView] = useState({ year: +today.slice(0, 4), month: +today.slice(5, 7) - 1 });
  const [dragId, setDragId] = useState<string | null>(null);

  const firstWeekday = new Date(Date.UTC(view.year, view.month, 1)).getUTCDay();
  const daysInMonth = new Date(Date.UTC(view.year, view.month + 1, 0)).getUTCDate();
  const monthLabel = new Date(Date.UTC(view.year, view.month, 1))
    .toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });

  const byDay = new Map<string, DashboardDeadline[]>();
  for (const d of deadlines) {
    if (!d.due_date || d.is_completed) continue;
    const k = d.due_date.slice(0, 10);
    if (!byDay.has(k)) byDay.set(k, []);
    byDay.get(k)!.push(d);
  }

  const cells: (string | null)[] = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(ymd(new Date(Date.UTC(view.year, view.month, day))));

  const prev = () => setView((v) => (v.month === 0 ? { year: v.year - 1, month: 11 } : { year: v.year, month: v.month - 1 }));
  const next = () => setView((v) => (v.month === 11 ? { year: v.year + 1, month: 0 } : { year: v.year, month: v.month + 1 }));

  const drop = (dayKey: string) => {
    const id = dragId;
    setDragId(null);
    if (!id) return;
    const d = deadlines.find((x) => x.id === id);
    if (!d || d.due_date?.slice(0, 10) === dayKey) return;
    if (window.confirm(`Move "${d.label}" (${d.deal_name}) to ${dayKey}?`)) onReschedule(d, dayKey);
  };

  const chipCls = (du: number | null) =>
    du !== null && du < 0 ? 'bg-primary-100 text-primary-500'
    : du !== null && du <= 7 ? 'bg-warning-wash text-warning'
    : 'bg-success-wash text-success';

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <button type="button" onClick={prev} className="h-[30px] rounded-lg border border-gray-200 px-2.5 text-[12.5px] text-gray-700 transition-colors hover:border-gray-900" aria-label="Previous month">‹</button>
        <span className="text-[13px] font-semibold text-gray-900">{monthLabel}</span>
        <button type="button" onClick={next} className="h-[30px] rounded-lg border border-gray-200 px-2.5 text-[12.5px] text-gray-700 transition-colors hover:border-gray-900" aria-label="Next month">›</button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-xs">
        {DOW.map((d) => <div key={d} className="py-1 text-center text-[9.5px] font-bold uppercase tracking-[.12em] text-gray-500">{d}</div>)}
        {cells.map((key, i) => (
          <div
            key={i}
            onDragOver={(e) => { if (key) e.preventDefault(); }}
            onDrop={() => key && drop(key)}
            className={`min-h-[72px] rounded-lg border p-1 ${key ? 'border-gray-150 bg-white' : 'border-transparent'}`}
          >
            {key && <div className="text-gray-400 text-[10px] mb-1">{Number(key.slice(8, 10))}</div>}
            {key && (byDay.get(key) || []).map((d) => (
              <div
                key={d.id}
                draggable
                onDragStart={() => setDragId(d.id)}
                className={`mb-1 px-1 py-0.5 rounded text-[10px] truncate cursor-move ${chipCls(d.days_until)}`}
                title={`${d.deal_name} · ${d.label}`}
              >
                {d.label}
              </div>
            ))}
          </div>
        ))}
      </div>
      <p className="mt-2 text-[11.5px] text-gray-500">Drag a deadline to another day to reschedule it.</p>
    </div>
  );
}
